import gsap from 'gsap';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';
import { getKeyboardFocusableElements, trapFocus } from '../utils.js';

export default class Menu {
	constructor() {
		this.element = document.querySelector('[data-menu]');

		if (!this.element) {
			return;
		}

		// Elements
		this.content = this.element.querySelector('[data-replace-content]');
		this.menuItems = this.content.querySelectorAll('[data-menu-item]');
		this.menuToggle = this.element.querySelector('[data-menu-toggle]');
		this.logo = this.element.querySelector('[data-menu-logo], .c-menu__logo');

		// Utils
		this.breakpoint = window.matchMedia('(min-width: 768px)');
		this.parser = new DOMParser();

		// State
		this.isOpen = false;
		this.firstLoad = true;
		this.trapFocus = null;

		// Events
		this.menuToggle.addEventListener('click', (e) => {
			this.isOpen ? this.close() : this.open();
		});

		this.initSubmenus();

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				this.close();
			}
		});

		this.breakpoint.addEventListener('change', (e) => {
			if (e.matches) {
				this.reset();
				gsap.set([this.content, this.menuItems], { clearProps: 'all' });
			}
		});

		document.addEventListener('bless', (e) => {
			const barbaNamespace = e.target?.dataset?.barbaNamespace;

			if (barbaNamespace) {
				this.element.setAttribute('data-menu', barbaNamespace);
			}

			if (this.firstLoad) {
				this.firstLoad = false;
				return;
			}

			this.updateContent(e.detail?.next?.html);
		});

		document.addEventListener('leave', (e) => {
			this.close();
		});
	}

	open() {
		if (this.isOpen) {
			return;
		}

		window.scrollTo(0, 0);

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.isOpen = true;
		this.element.classList.add('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'true');

		this.trapMenu();
		disablePageScroll();

		gsap.set(this.menuItems, {
			autoAlpha: 0,
			y: 10,
		});

		tl.to(this.content, {
			autoAlpha: 1,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'power1.out',
		});

		tl.to(
			this.menuItems,
			{
				y: 0,
				autoAlpha: 1,
				stagger: prefersReducedMotion ? 0 : 0.1,
				duration: prefersReducedMotion ? 0 : 0.8,
				ease: 'power4.out',
			},
			'<+=0.25'
		);
	}

	close() {
		if (!this.isOpen) {
			return;
		}

		this.reset();

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		gsap.set(this.menuItems, {
			autoAlpha: 1,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
		});

		tl.to(
			this.content,
			{
				autoAlpha: 0,
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
			},
			'<+=0.25'
		);
	}

	reset() {
		this.isOpen = false;
		this.element.classList.remove('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'false');

		this.trapFocus?.remove();
		this.trapFocus = null;

		enablePageScroll();
	}

	trapMenu() {
		this.trapFocus = trapFocus(this.element, [
			this.menuToggle,
			...getKeyboardFocusableElements(this.content),
			// this.logo,
		]);
	}

	updateContent(html) {
		if (!html) {
			return;
		}

		const parsed = this.parser.parseFromString(html, 'text/html');
		const newMenuRoot = parsed.querySelector('[data-menu]');
		const newMenu = parsed.querySelector('[data-menu] [data-replace-content]');
		const newLogo = newMenuRoot?.querySelector('[data-menu-logo]');

		if (!newMenu) {
			return;
		}

		if (this.logo && newLogo) {
			this.logo.innerHTML = newLogo.innerHTML;

			const logoVideo = this.logo.querySelector('.c-menu__logo-video--kd');
			if (logoVideo) {
				logoVideo.load();
				logoVideo.play().catch(() => {});
			}
		}

		// Update menu items
		const newMenuItems = newMenu.querySelectorAll('[data-menu-item]');

		this.menuItems.forEach((item) => {
			item.classList.remove('is-active');
		});

		newMenuItems.forEach((item) => {
			const href = this.getMenuItemHref(item);
			const newLinkActive = item.classList.contains('is-active');
			const currentLink = [...this.menuItems].find((oldLink) => this.getMenuItemHref(oldLink) === href);

			if (currentLink && newLinkActive) {
				currentLink.classList.add('is-active');
			}
		});

		this.initSubmenus();
	}

	initSubmenus() {
		const parents = this.element.querySelectorAll('[data-menu-parent]');

		parents.forEach((parent) => {
			const trigger = parent.querySelector('[data-menu-trigger]');
			const subItems = parent.querySelectorAll('[data-menu-sub-item]');
			const subList = parent.querySelector('[data-menu-sub-list]');

			if (!trigger || !subItems.length) return;

			const isInitialized = parent.dataset.submenuInitialized === 'true';

			this.setupAttributes(parent, trigger, subList);
			parent.timeline = this.createAnimation(subItems);
			this.toggleSubmenuState(parent, false);

			if (isInitialized) {
				return;
			}

			parent.dataset.submenuInitialized = 'true';

			parent.addEventListener('mouseenter', () => {
				if (this.breakpoint.matches) this.openSubmenu(parent);
			});

			parent.addEventListener('mouseleave', () => {
				if (this.breakpoint.matches) this.closeSubmenu(parent);
			});

			parent.addEventListener('focusout', (event) => {
				if (!parent.contains(event.relatedTarget)) {
					this.closeSubmenu(parent);
				}
			});

			trigger.addEventListener('click', (event) => {
				const isOpen = parent.classList.contains('is-sub-open');
				isOpen ? this.closeSubmenu(parent) : this.openSubmenu(parent);
			});

			trigger.addEventListener('keydown', (event) => {
				if (event.key === 'Escape') {
					this.closeSubmenu(parent);
					trigger.focus();
				}

				if ((event.key === 'Enter' || event.key === ' ') && subItems.length) {
					event.preventDefault();
					this.openSubmenu(parent);
					parent.querySelector('[data-menu-sub-link]')?.focus();
				}
			});
		});
	}

	openSubmenu(parent) {
		const trigger = parent.querySelector('[data-menu-trigger]');

		parent.classList.add('is-sub-open');
		trigger.setAttribute('aria-expanded', 'true');

		this.toggleSubmenuState(parent, true);

		if (this.breakpoint.matches && parent.timeline) {
			parent.timeline.play();
		}

		this.updateSubmenuOverlay();
	}

	closeSubmenu(parent) {
		const trigger = parent.querySelector('[data-menu-trigger]');

		parent.classList.remove('is-sub-open');
		trigger.setAttribute('aria-expanded', 'false');

		this.toggleSubmenuState(parent, false);

		if (this.breakpoint.matches && parent.timeline) {
			parent.timeline.reverse();
		}

		this.updateSubmenuOverlay();
	}

	setupAttributes(parent, trigger, subList) {
		if (!parent.dataset.submenuId) {
			parent.dataset.submenuId = `submenu-${Math.random().toString(36).slice(2, 9)}`;
		}

		if (subList && !subList.id) {
			subList.id = parent.dataset.submenuId;
		}

		trigger.setAttribute('aria-haspopup', 'true');
		if (subList) {
			trigger.setAttribute('aria-controls', subList.id);
		}
	}

	getMenuItemHref(item) {
		const trigger = item.querySelector('[data-menu-trigger]');
		if (!trigger) return null;
		if (trigger.tagName === 'A') {
			return trigger.getAttribute('href');
		}
		return trigger.dataset.originalHref || null;
	}

	createAnimation(subItems) {
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		return gsap.timeline({ paused: true }).to(subItems, {
			opacity: 1,
			y: 0,
			stagger: prefersReducedMotion ? 0 : 0.05,
			duration: prefersReducedMotion ? 0 : 0.4,
			ease: 'power2.out',
		});
	}

	toggleSubmenuState(parent, isOpen) {
		const subItems = parent.querySelectorAll('[data-menu-sub-item]');
		const subLinks = parent.querySelectorAll('[data-menu-sub-link]');
		const subList = parent.querySelector('[data-menu-sub-list]');

		subLinks.forEach((link) => link.setAttribute('tabindex', isOpen ? '0' : '-1'));
		if (subList) subList.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

		if (!this.breakpoint.matches) {
			gsap.set(subItems, {
				opacity: isOpen ? 1 : 0,
				y: isOpen ? 0 : 5,
			});
		}
	}

	updateSubmenuOverlay() {
		const hasOpenSubmenu = this.element.querySelector('.is-sub-open');
		document.body.classList.toggle('has-submenu-open', Boolean(hasOpenSubmenu));
	}
}
